import { useCallback } from "react";

import { dashboardPlatformsControllerAvailableSearch } from "@/api/__generated__/dashboard-platforms/dashboard-platforms";
import { useT } from "@/i18n";
import { mapToPlatformReference } from "@/mappers";
import { PlatformReference } from "@/types";
import { AsyncInlineCommand, AsyncInlineCommandProps } from "@/ui";

import { PlatformLogo } from "./PlatformLogo";

const SEARCH_LIMIT = 25;
const MIN_KEYWORD_LENGTH = 2;

export type AvailablePlatformAsyncInlineCommandProps = Omit<
  AsyncInlineCommandProps<PlatformReference, number>,
  "loadOptions" | "getOptionValue" | "getOptionLabel" | "getOptionIcon" | "label" | "placeholder"
> & {
  label?: string;
  placeholder?: string;
  excludePlatformIds?: string[];
};

export const AvailablePlatformAsyncInlineCommand = ({
  label,
  placeholder,
  excludePlatformIds,
  ...props
}: AvailablePlatformAsyncInlineCommandProps) => {
  const t = useT();

  const loadPlatforms = useCallback(
    async (query: string, options?: { cursor?: number; signal?: AbortSignal }) => {
      const page = options?.cursor ?? 1;
      const trimmed = query.trim();
      const keyword = trimmed.length >= MIN_KEYWORD_LENGTH ? trimmed : undefined;

      // `/available/search` always returns slim refs — `include` is not declared on the
      // available input class, so it cannot be set here at the type level.
      const result = await dashboardPlatformsControllerAvailableSearch(
        {
          ...(keyword && { keyword }),
          page,
          limit: SEARCH_LIMIT,
        },
        undefined,
        options?.signal
      );

      const items = result.data
        .map(mapToPlatformReference)
        .filter((platform) => !excludePlatformIds?.includes(platform.id));
      const hasMore = page < result.meta.totalPages;

      return { items, cursor: hasMore ? page + 1 : null };
    },
    [excludePlatformIds]
  );

  return (
    <AsyncInlineCommand<PlatformReference, number>
      loadOptions={loadPlatforms}
      loadOnMount
      getOptionValue={(platform) => String(platform.id)}
      getOptionLabel={(platform) => platform.title}
      getOptionIcon={(platform, { width, height }) => (
        <PlatformLogo platform={platform} width={width} height={height} />
      )}
      label={label ?? t("platform.components.globalSelect.label")}
      placeholder={placeholder ?? t("platform.components.globalSelect.placeholder")}
      {...props}
    />
  );
};
